import React, { createContext, useEffect, ReactNode } from 'react'
import { AccountResponseItem, useAccounts } from 'hooks/useAccounts'
import { EditPendingAccountParams } from 'types/accountTypes'

interface AccountContextType {
  accounts: AccountResponseItem[]
  totalPendingCount: number
  totalPages: number
  currentPage: number
  status: string
  isLoading: boolean
  error: string | null
  fetchAccounts: (email?: string, status?: string, page?: number) => void
  setStatus: (status: string) => void
  updatePendingAccount: (data: EditPendingAccountParams) => Promise<{
    success: boolean
    error?: string
  }>
  logout: () => void
  login: (email: string, password: string) => Promise<void>
  isAuthenticated: boolean
}

export const AccountContext = createContext<AccountContextType>({
  accounts: [],
  totalPendingCount: 0,
  totalPages: 1,
  currentPage: 1,
  status: '',
  isLoading: false,
  error: null,
  fetchAccounts: () => {
    throw new Error('fetchAccounts method not implemented')
  },
  setStatus: () => {
    throw new Error('setStatus method not implemented')
  },
  updatePendingAccount: () => {
    throw new Error('updatePendingAccount method not implemented')
  },
  logout: () => {
    throw new Error('logout method not implemented')
  },
  login: async () => {
    throw new Error('login method not implemented')
  },
  isAuthenticated: false
})

export const AccountsProvider = ({ children }: { children: ReactNode }) => {
  const {
    accounts,
    totalPages,
    currentPage,
    status,
    isLoading,
    error,
    totalPendingCount,
    fetchAccounts,
    setStatus,
    updatePendingAccount,
    logout,
    login,
    isAuthenticated
  } = useAccounts()

  useEffect(() => {
    if (isAuthenticated) {
      fetchAccounts('', status, 1)
    }
  }, [status, isAuthenticated])

  return (
    <AccountContext.Provider
      value={{
        accounts,
        totalPendingCount,
        totalPages,
        currentPage,
        status,
        isLoading,
        error,
        fetchAccounts,
        setStatus,
        updatePendingAccount,
        logout,
        login,
        isAuthenticated
      }}
    >
      {children}
    </AccountContext.Provider>
  )
}
